import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Shield, Settings, Trash2 } from 'lucide-react';

export default function GroupsManagementPage() {
  const { user } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupRoles, setGroupRoles] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [newGroupData, setNewGroupData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchGroups();
    fetchAllRoles();
    fetchAllUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/admin/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups', error);
      toast.error('Errore nel caricamento gruppi');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRoles = async () => {
    try {
      const response = await axios.get('/admin/roles');
      setAllRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      console.log('Fetched users:', response.data.length, 'users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
      if (error.response?.status === 403) {
        toast.error('Permesso ADMIN_GESTIONE_PERMESSI richiesto per visualizzare tutti gli utenti');
      } else {
        toast.error(getErrorMessage(error, 'Errore nel caricamento degli utenti'));
      }
    }
  };

  const fetchGroupRoles = async (groupId) => {
    try {
      const response = await axios.get(`/admin/groups/${groupId}/roles`);
      setGroupRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch group roles', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/admin/groups', null, {
        params: { 
          name: newGroupData.name, 
          description: newGroupData.description 
        }
      });
      toast.success('Gruppo creato con successo!');
      setCreateDialogOpen(false);
      setNewGroupData({ name: '', description: '' });
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione gruppo'));
    }
  };

  const handleOpenRolesDialog = async (group) => {
    setSelectedGroup(group);
    setRoleSearchTerm('');
    await fetchGroupRoles(group.id);
    setRolesDialogOpen(true);
  };

  const handleToggleRole = async (roleId, isAssigned) => {
    try {
      if (isAssigned) {
        await axios.delete(`/admin/groups/${selectedGroup.id}/roles/${roleId}`);
        toast.success('Ruolo rimosso');
      } else {
        await axios.post(`/admin/groups/${selectedGroup.id}/roles/${roleId}`);
        toast.success('Ruolo assegnato');
      }
      await fetchGroupRoles(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'operazione'));
    }
  };

  const handleAssignUserToGroup = async (userId, groupId) => {
    try {
      console.log('Assigning user', userId, 'to group', groupId);
      await axios.put(`/admin/users/${userId}/group`, null, {
        params: { group_id: groupId }
      });
      toast.success('Utente assegnato al gruppo con successo');
      await fetchAllUsers();
      await fetchGroups();
    } catch (error) {
      console.error('Error assigning user to group:', error);
      toast.error(getErrorMessage(error, 'Errore nell\'assegnazione utente al gruppo'));
    }
  };

  const handleRemoveUserFromGroup = async (userId) => {
    try {
      await axios.delete(`/admin/users/${userId}/group`);
      toast.success('Utente rimosso dal gruppo');
      await fetchAllUsers();
      await fetchGroups();
    } catch (error) {
      console.error('Error removing user from group:', error);
      toast.error(getErrorMessage(error, 'Errore nella rimozione dell\'utente dal gruppo'));
    }
  };

  const isRoleAssigned = (roleId) => {
    return groupRoles.some(r => r.id === roleId);
  };

  const filteredRoles = allRoles.filter((role) => {
    const search = roleSearchTerm.trim().toLowerCase();
    if (!search) return true;
    return (
      role.code.toLowerCase().includes(search) ||
      (role.name || '').toLowerCase().includes(search) ||
      (role.description || '').toLowerCase().includes(search)
    );
  });
  const assignedRoles = filteredRoles.filter((role) => isRoleAssigned(role.id));
  const unassignedRoles = filteredRoles.filter((role) => !isRoleAssigned(role.id));

  const searchableUsers = allUsers
    .filter((u) => {
      const search = userSearchTerm.trim().toLowerCase();
      if (!search) return true;
      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      const username = (u.username || u.email || u.phone_number || '').toLowerCase();
      return fullName.includes(search) || username.includes(search);
    });
  const assignedUsers = searchableUsers.filter((u) => u.group_id === selectedGroup?.id);
  const unassignedUsers = searchableUsers.filter((u) => u.group_id !== selectedGroup?.id);

  return (
    <div className="min-h-screen bg-white" data-testid="groups-management-page">
      <Navbar />

      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="page-title">
              Gestione gruppi e ruoli
            </h1>
            <p className="text-gray-600">Configura permessi e gruppi utenti</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto">
                + Nuovo gruppo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea nuovo gruppo</DialogTitle>
                <DialogDescription>
                  Crea un gruppo e assegna i ruoli necessari
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome gruppo *</Label>
                  <Input
                    id="name"
                    required
                    value={newGroupData.name}
                    onChange={(e) => setNewGroupData({...newGroupData, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    rows={3}
                    value={newGroupData.description}
                    onChange={(e) => setNewGroupData({...newGroupData, description: e.target.value})}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                    Crea gruppo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <Card key={group.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{group.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {group.description}
                        </CardDescription>
                      </div>
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Utenti:</span>
                        <span className="font-semibold">{group.user_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ruoli:</span>
                        <span className="font-semibold">{group.role_count}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                      <Button 
                        className="w-full sm:flex-1 bg-black text-white hover:bg-gray-800"
                        onClick={() => handleOpenRolesDialog(group)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Gestisci ruoli
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full sm:flex-1"
                        onClick={() => {
                          setSelectedGroup(group);
                          setUsersDialogOpen(true);
                        }}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Gestione utenti
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Roles Management Dialog */}
            <Dialog 
              open={rolesDialogOpen} 
              onOpenChange={(open) => {
                setRolesDialogOpen(open);
                if (!open) setRoleSearchTerm('');
              }}
            >
              <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Gestisci ruoli - {selectedGroup?.name}</DialogTitle>
                  <DialogDescription>
                    Seleziona i ruoli da assegnare a questo gruppo
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="search-role">Cerca ruolo</Label>
                    <Input
                      id="search-role"
                      placeholder="Digita codice, nome o descrizione"
                      value={roleSearchTerm}
                      onChange={(e) => setRoleSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-lg">
                      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
                        <p className="font-semibold">Disponibili</p>
                        <span className="text-sm text-gray-500">{unassignedRoles.length}</span>
                      </div>
                      <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {unassignedRoles.length === 0 ? (
                          <p className="text-sm text-gray-500 p-3">Nessun ruolo disponibile</p>
                        ) : (
                          unassignedRoles.map((role) => (
                            <div 
                              key={role.id} 
                              className="flex items-center justify-between p-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 pr-3">
                                <p className="font-semibold">{role.code}</p>
                                <p className="text-sm text-gray-600">{role.description}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleToggleRole(role.id, false)}
                              >
                                Aggiungi
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="border rounded-lg">
                      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
                        <p className="font-semibold">Assegnati</p>
                        <span className="text-sm text-gray-500">{assignedRoles.length}</span>
                      </div>
                      <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {assignedRoles.length === 0 ? (
                          <p className="text-sm text-gray-500 p-3">Nessun ruolo assegnato</p>
                        ) : (
                          assignedRoles.map((role) => (
                            <div 
                              key={role.id} 
                              className="flex items-center justify-between p-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 pr-3">
                                <p className="font-semibold">{role.code}</p>
                                <p className="text-sm text-gray-600">{role.description}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleToggleRole(role.id, true)}
                              >
                                Rimuovi
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Users Assignment Dialog */}
            <Dialog 
              open={usersDialogOpen} 
              onOpenChange={(open) => {
                setUsersDialogOpen(open);
                if (!open) setUserSearchTerm('');
              }}
            >
              <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Utenti - {selectedGroup?.name}</DialogTitle>
                  <DialogDescription>
                    Assegna utenti a questo gruppo ({searchableUsers.length} utenti disponibili)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="search-user">Cerca utente</Label>
                    <Input
                      id="search-user"
                      placeholder="Digita nome o username"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-lg">
                      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
                        <p className="font-semibold">Non assegnati</p>
                        <span className="text-sm text-gray-500">{unassignedUsers.length}</span>
                      </div>
                      <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {unassignedUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 p-3">Nessun utente da assegnare</p>
                        ) : (
                          unassignedUsers.map((u) => (
                            <div 
                              key={u.id} 
                              className="flex items-center justify-between p-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 pr-3">
                                <p className="font-semibold">
                                  {u.first_name} {u.last_name}
                                </p>
                                <p className="text-sm text-gray-600">{u.username || u.email}</p>
                                {u.group_id && u.group_id !== selectedGroup?.id && (
                                  <p className="text-xs text-gray-500">
                                    Gruppo attuale: {groups.find(g => g.id === u.group_id)?.name}
                                  </p>
                                )}
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAssignUserToGroup(u.id, selectedGroup?.id)}
                              >
                                Assegna
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="border rounded-lg">
                      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
                        <p className="font-semibold">Assegnati</p>
                        <span className="text-sm text-gray-500">{assignedUsers.length}</span>
                      </div>
                      <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {assignedUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 p-3">Nessun utente assegnato</p>
                        ) : (
                          assignedUsers.map((u) => (
                            <div 
                              key={u.id} 
                              className="flex items-center justify-between p-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 pr-3">
                                <p className="font-semibold">
                                  {u.first_name} {u.last_name}
                                </p>
                                <p className="text-sm text-gray-600">{u.username || u.email}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRemoveUserFromGroup(u.id)}
                              >
                                Rimuovi
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
}
